import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpDown } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { DataTable } from "../../components/DataTable";
import { Button } from "../../components/ui/button";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../../lib/api";
import SkeletonTable from "../SkeletonTable";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { DialogDelete } from "../../components/Common/DialogDelete";
import { formatDate, formatEGP } from "../../lib/utils";
import { Avatar } from "antd";
import { TransactionEditSheet } from './_components/TransactionEditSheet';
import { TransactionAddSheet } from './_components/TransactionAddSheet';

export default function Transaction() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Users
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const handleFetchUsers = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/users`);
      setUsers(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadingUsers(false);
    }
  };

  // Transactions
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const handleFetchTransactions = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/transactions`);
      setTransactions(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    setLoadingTransactions(true);
    handleFetchTransactions();
    setLoadingUsers(true);
    handleFetchUsers();
  }, []);

  const columns = [
    {
      accessorKey: "user.name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          الموظف
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Avatar
            className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300"
            src={`${UrlImage}${row.original.user.image}`}
          >
            {row.original.user.name?.charAt(0)}
          </Avatar>
          <div className="text-right">
            <div className="font-medium">{row.original.user.name}</div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "created_at",
      header: "تاريخ المعاملة",
      cell: ({ row }) =>
        row.original.created_at ? formatDate(row.original.created_at) : "___",
    },
    {
      accessorKey: "amount",
      header: "المبلغ",
      cell: ({ row }) => {
        const amount = Number(row.original.amount);
        return formatEGP(amount);
      },
    },
    {
      accessorKey: "reason",
      header: "السبب",
      cell: ({ row }) => (
        <div className="text-right">{row.original.reason || "___"}</div>
      ),
    },

    {
      accessorKey: "type",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          النوع
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <span className="text-center">{row.original.type}</span>
      ),
    },
    {
      id: "Actions",
      header: "الإجراءات",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {!loading && user?.role === "admin" && (
            <>
              <TransactionEditSheet
                selectedRecord={row.original}
                handleFetchTransactions={handleFetchTransactions}
                loadingUsers={loadingUsers}
                users={users}
              />
              <DialogDelete
                title={"المعاملة المالية"}
                apiUrl={`/transactions/${row.original.bid}`}
                fetchData={handleFetchTransactions}
              />
            </>
          )}
        </div>
      ),
    },
  ];

  const exportData = transactions.map((row) => {
    const amount = Number(row.amount);
    return {
      user: row.user?.name || "",
      email: row.user?.email || "",
      type: row.type,
      amount: formatEGP(amount),
      reason: row.reason || "",
      transaction_date: row.created_at ? formatDate(row.created_at) : "___",
    };
  });

  const exportColumns = [
    { accessorKey: "user", header: "الموظف" },
    { accessorKey: "email", header: "البريد الإلكتروني" },
    { accessorKey: "type", header: "النوع" },
    { accessorKey: "amount", header: "المبلغ" },
    { accessorKey: "reason", header: "السبب" },
    { accessorKey: "transaction_date", header: "تاريخ المعاملة" },
  ];

  const [dateFilter, setDateFilter] = useState(null);

  function filterByCreatedAt(data, date = null) {
    if (!date) {
      return data;
    }

    // return data.filter((item) => {
    //   const createdDate = item.created_at.split("T")[0];
    //   return createdDate === date;
    // });
    return data.filter((item) => item.created_at.includes(date));
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ type: "spring", duration: 2 }}
      dir="rtl"
      className="space-y-5"
    >
      <div className="flex items-center gap-2 justify-between">
        <h1 className="text-2xl font-bold">المعاملات المالية</h1>
        {/* <Button onClick={() => navigate("/transaction-add")}>إضافة سجل</Button> */}
        <TransactionAddSheet
          handleFetchTransactions={handleFetchTransactions}
          users={users}
          loadingUsers={loadingUsers}
        />
      </div>

      {loadingTransactions ? (
        <SkeletonTable />
      ) : (
        <DataTable
          columns={columns}
          data={filterByCreatedAt(transactions, dateFilter)}
          title={"المعاملات المالية"}
          filterNames={["user_name", "user_email", "type"]}
          exportData={exportData}
          exportColumns={exportColumns}
          setDateFilter={setDateFilter}
          useDateFilter={true}
        />
      )}
    </motion.div>
  );
}
